import { AfterViewInit, ChangeDetectionStrategy, Component, forwardRef, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { ICurrency, IDataService, IParsedDataResponse } from '../../../../services/data/data-service.interface';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DestroySubscribers } from '../../../../decorators/destroy-subscribers.decorator';
import { Subscription } from 'rxjs';
import { InfoModal } from '../../../../modals/info/info.modal';
import { MODAL_TOOLTIP, MODAL_TOOLTIP_KEY_ENUM } from '../../../form-fields/controls-with-hint/hints';
import { IMODALS_SERVICE, IModalsService } from '@platform500services/p500-ui-kit';
import { first } from 'rxjs/operators';
import { NgSelectComponent } from '@ng-select/ng-select';

@Component({
  selector: 'gtd-currency-select',
  templateUrl: './currency-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CurrencySelectComponent),
      multi: true,
    },
  ],
})
@DestroySubscribers()
export class CurrencySelectComponent implements OnInit, AfterViewInit, ControlValueAccessor {
  @Input() acceptedCurrencyList: ICurrency | null;
  @Input() multiple = false;
  @Input() label = $localize`:@@common.currency:Currency`;
  @Input() shortView = false;
  @Input() showHint = false;
  @Input() tooltipKey = MODAL_TOOLTIP_KEY_ENUM.CURRENCY_SELECT;
  @Input() clearable = false;
  @Input() showWarning = false;
  @ViewChild(NgSelectComponent) selector: NgSelectComponent;
  public currencyList: ICurrency;
  public currencyControl = new FormControl<keyof ICurrency | null>(null);
  public initialValue: string;
  public data: Record<string, string>;
  private onTouched: () => any = () => {};
  private onChange: (value: any) => any = () => {};
  private subscriptions: Record<string, Subscription> = {};
  private globalData: IParsedDataResponse;
  private defaultCurrency: string;

  constructor(@Inject('IDataService') private dataService: IDataService, @Inject(IMODALS_SERVICE) public modalsService: IModalsService) {
    this.globalData = this.dataService.getData();
  }

  public ngOnInit(): void {
    this.currencyList = this.globalData.currency_list;
    this.data = Object.entries(this.acceptedCurrencyList || this.currencyList).reduce((data, pair) => {
      data[pair[0]] = pair[1].name;
      return data;
    }, {});
    this.defaultCurrency = this.globalData.default_currency.currency;
    this.subscriptions.currencyControlChanges = this.currencyControl.valueChanges.subscribe((code) => {
      this.onChange(code);
    });
    this.subscriptions.currencyControlInitialValue = this.currencyControl.valueChanges.pipe(first()).subscribe((value: string) => {
      this.initialValue = value;
    });
  }

  public ngAfterViewInit(): void {
    if (!this.currencyControl.value) {
      this.currencyControl.setValue(this.defaultCurrency);
    }
  }

  public writeValue(code: keyof ICurrency | null): void {
    this.currencyControl.setValue(code || this.defaultCurrency);
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public onTouch(): void {
    this.onTouched();
  }

  public openTooltipModal(): void {
    this.modalsService.open(InfoModal, {
      data: {
        title: $localize`:@@common.help_information:Help Information`,
        subTitle: '',
        body: MODAL_TOOLTIP[this.tooltipKey],
        footerType: null,
        hasCopy: false,
        class: '-info',
      },
    });
  }
}
